/*
  B2B audience cards.

  Two behaviours, both driven from the section root so a `shopify:section:load` replacement
  needs no rebinding:

    1. A native horizontal scroll track with CSS scroll-snap. Swipe, trackpad, wheel and
       keyboard focus all move it without JavaScript; this file only reflects the scroll
       position in the arrows and the scrollbar, and makes the scrollbar draggable.

    2. A per-card expand toggle. Each card expands independently — see assets/b2b-audience-cards.css
       for why the track must not stretch its items.

  Adapted from assets/ml013-category-carousel.js, with two deliberate differences:
    - a ResizeObserver replaces that file's `window` resize listener, which is added per init and
      never removed, so a theme-editor session accumulates listeners bound to detached nodes;
    - the scrollbar is draggable and keyboard-operable, because a visible thumb promises drag.
*/

(function () {
  if (window.__b2bAudienceCards) {
    return;
  }

  window.__b2bAudienceCards = true;

  function clamp(value, min, max) {
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function update(state) {
    var track = state.track;
    var max = track.scrollWidth - track.clientWidth;
    var left = track.scrollLeft;
    var overflows = max > 1;
    var ratio = overflows ? clamp(left / max, 0, 1) : 0;

    // Lets the CSS hide the arrows and scrollbar entirely when every card already fits.
    state.root.classList.toggle('is-static', !overflows);

    if (state.prev) {
      state.prev.disabled = !overflows || left <= 1;
    }
    if (state.next) {
      state.next.disabled = !overflows || left >= max - 1;
    }

    if (state.scrollbar && state.thumb) {
      var widthPercent = track.scrollWidth ? (track.clientWidth / track.scrollWidth) * 100 : 100;

      if (widthPercent > 100) {
        widthPercent = 100;
      }

      state.thumb.style.width = widthPercent + '%';
      state.thumb.style.marginLeft = ratio * (100 - widthPercent) + '%';
      state.scrollbar.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    }
  }

  function schedule(state) {
    if (state.pending) {
      return;
    }

    state.pending = true;

    requestAnimationFrame(function () {
      state.pending = false;
      update(state);
    });
  }

  function bindToggles(state) {
    // Delegated: one listener regardless of card count, and it survives card markup changes.
    state.root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-b2b-cards-toggle]');

      if (!button || !state.root.contains(button)) {
        return;
      }

      var card = button.closest('[data-b2b-cards-card]');

      if (!card) {
        return;
      }

      setExpanded(state, card, button, button.getAttribute('aria-expanded') !== 'true');
    });

    // The Figma's expanded card (1111:581) has no collapse link — the toggle is hidden once the
    // card opens. Escape still closes it, and focus returns to the toggle it came from.
    state.root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') {
        return;
      }

      var card = event.target.closest('[data-b2b-cards-card].is-expanded');
      var button = card && card.querySelector('[data-b2b-cards-toggle]');

      if (button) {
        setExpanded(state, card, button, false);
        button.focus();
      }
    });
  }

  function setExpanded(state, card, button, expand) {
    var hadFocus = document.activeElement === button;

    button.setAttribute('aria-expanded', expand ? 'true' : 'false');
    card.classList.toggle('is-expanded', expand);

    var label = button.querySelector('[data-b2b-cards-toggle-label]');

    if (label) {
      label.textContent = expand ? button.dataset.labelLess : button.dataset.labelMore;
    }

    // The toggle disappears when the card opens, so keyboard focus moves into what it revealed.
    if (expand && hadFocus) {
      var reveal = document.getElementById(button.getAttribute('aria-controls'));

      if (reveal) {
        reveal.focus({ preventScroll: true });
      }
    }

    // The card's height just changed, so the scrollbar thumb needs recomputing.
    schedule(state);
  }

  function bindScrollbar(state) {
    var bar = state.scrollbar;

    if (!bar) {
      return;
    }

    var dragging = false;

    function scrollToPointer(clientX) {
      var rect = bar.getBoundingClientRect();
      var thumbWidth = state.thumb ? state.thumb.getBoundingClientRect().width : 0;
      var usable = rect.width - thumbWidth;

      if (usable <= 0) {
        return;
      }

      var offset = clamp(clientX - rect.left - thumbWidth / 2, 0, usable);
      var max = state.track.scrollWidth - state.track.clientWidth;

      state.track.scrollLeft = (offset / usable) * max;
    }

    bar.addEventListener('pointerdown', function (event) {
      if (state.root.classList.contains('is-static')) {
        return;
      }

      dragging = true;

      try {
        bar.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is best-effort */
      }

      scrollToPointer(event.clientX);
      event.preventDefault();
    });

    bar.addEventListener('pointermove', function (event) {
      if (dragging) {
        scrollToPointer(event.clientX);
      }
    });

    function endDrag(event) {
      if (!dragging) {
        return;
      }

      dragging = false;

      try {
        bar.releasePointerCapture(event.pointerId);
      } catch (error) {
        /* capture is best-effort */
      }
    }

    bar.addEventListener('pointerup', endDrag);
    bar.addEventListener('pointercancel', endDrag);

    bar.addEventListener('keydown', function (event) {
      var direction = 0;

      if (event.key === 'ArrowRight') {
        direction = 1;
      } else if (event.key === 'ArrowLeft') {
        direction = -1;
      }

      if (!direction) {
        return;
      }

      event.preventDefault();
      state.step(direction);
    });
  }

  function init(root) {
    if (root.__b2bCards) {
      return;
    }

    var track = root.querySelector('[data-b2b-cards-track]');

    if (!track) {
      return;
    }

    var state = {
      root: root,
      track: track,
      prev: root.querySelector('[data-b2b-cards-prev]'),
      next: root.querySelector('[data-b2b-cards-next]'),
      scrollbar: root.querySelector('[data-b2b-cards-scrollbar]'),
      thumb: root.querySelector('[data-b2b-cards-thumb]'),
      pending: false
    };

    root.__b2bCards = state;

    state.step = function (direction) {
      // The CSS `scroll-behavior` rule does not apply to an explicit `behavior` option,
      // so the reduced-motion preference has to be read here too.
      track.scrollBy({
        left: direction * track.clientWidth,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    };

    if (state.prev) {
      state.prev.addEventListener('click', function () {
        state.step(-1);
      });
    }

    if (state.next) {
      state.next.addEventListener('click', function () {
        state.step(1);
      });
    }

    track.addEventListener(
      'scroll',
      function () {
        schedule(state);
      },
      { passive: true }
    );

    // Disconnects with the node, unlike a window listener, and additionally fires when a card
    // expands or the mobile layout switches between stacked and carousel.
    if (window.ResizeObserver) {
      state.observer = new ResizeObserver(function () {
        schedule(state);
      });
      state.observer.observe(track);
    } else {
      window.addEventListener('resize', function () {
        schedule(state);
      });
    }

    bindToggles(state);
    bindScrollbar(state);
    update(state);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-b2b-cards]');

    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
